import {
  Component,
  OnInit,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  Renderer2,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  HostListener,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, ValidationErrors } from '@angular/forms';
import { DROPDOWN_SORT } from 'src/app/dataModels/constants';
import { DomSanitizer } from '@angular/platform-browser';
import { SelectItemGroup } from 'primeng/api';

/**
 * Wrapper component around Primeng Dropdown and MultiSelect
 */
@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class SelectComponent implements OnInit, ControlValueAccessor, OnChanges {
  /**
   * temp variable to hold the value to write to dropdown
   */
  valueToWrite: any;

  /**
   * List of options for dropdown
   */
  @Input()
  options = [];

  /**
   * Label to display when nothing is selected in dropdown
   */
  @Input()
  defaultLabel = 'Select';

  /**
   * Indicate if dropdown will be displayed as Single Select or MultiSelect
   */
  @Input()
  isMultiSelect = false;

  /**
   * property to be used to be displayed as dropdown options, default is 'descr' property of the LOV list
   */
  @Input()
  label = 'descr';

  /**
   * property to be used to be bind value to the dropdown and return the value when dropdown option is selected
   * default is 'columnValue'
   */
  @Input()
  optionValue = 'columnValue';

  /**
   * indicate if property is disabled
   */
  @Input()
  disabled = false;

  /**
   * indicate if control is invalid
   */
  @Input()
  isInvalid: ValidationErrors | boolean;

  /**
   * indicate if control is readonly
   */
  @Input()
  readOnly = false;

  /**
   * sorting of the dropdown list, default is by 'displayOrder'
   */
  @Input()
  orderBy = DROPDOWN_SORT.DISPLAY_ORDER;

  /**
   * render dropdown with customTemplate
   */
  @Input() useCustomTemplate = false;

  /**
   * used to render groupedOptions
   */
  @Input() groupedOptions: SelectItemGroup[] = [];

  /**
   * indicate if dropdown has to be grouped
   */
  @Input() group = false;

  /**
   * limit selections for multiselect dropdown
   */
  @Input() limitTo;

  /**
   * enable checkbox
   */
  @Input() selectAllRequired = false;
  /**
   * limit selections for multiselect dropdown
   */
  @Input() isPopUp = false;

  /**
   * display max selected labels
   */
  @Input() maxSelectedLabels = 3;

  /**
   * on change event of the dropdown
   */
  private onChange;

  /**
   * on touched event of the dropdown
   */
  private onTouched;

  /**
   * value to be bound to the dropdown
   */
  private _value: any;

  /**
   * change event broadcast when dropdown value is changed
   */
  @Output() changeEvent = new EventEmitter();

  /**
   * indicate if dropdown has to sorted in reverse
   */
  @Input() reverse = false;

  /**
   * indicate if dropdown value can be cleared
   */
  @Input() showClear = true;

  /**
   * touch event handler
   */
  private onTouch: any = () => {};

  /**
   * getter for the value
   */
  @Input()
  get value(): any {
    return this._value;
  }

  /**
   * setter for the value
   */
  set value(value: any) {
    if (value !== this._value) {
      this._value = value;
      const returnVal = this.optionValue && !this.isMultiSelect && !this.group && this._value ? this.getOptionValue() : this._value;
      this.onChange(returnVal);
    }
  }

  /**
   * constructor to inject dependency
   * @param renderer instance of renderer2
   * @param element instance of element
   */
  constructor(private renderer: Renderer2, private element: ElementRef, private sanitizer: DomSanitizer) {
    this.onChange = (_: any) => {};
    this.onTouched = () => {};
  }

  /**
   * initialize component
   */
  ngOnInit() {
   // console.log(this.options);
    // this.addSelect();
  }

  /**
   * set the value to the dropdown
   * @param obj value to be written to the dropdown
   */
  writeValue(obj: any): void {
    this.value =
      this.optionValue && !this.isMultiSelect && !this.group && this.options ? this.getOption(obj) : this.getMultiselectOption(obj);
    this.valueToWrite = obj;
  }

  /**
   * for the value to be set, get the listItem from the list bound to dropdown else return null
   * @param obj value to be written to the dropdown
   */
  getOption(obj) {
    let selectedList = [];
    if(this.optionValue.indexOf('.') !== -1) {
      selectedList = this.options.filter(x => this.optionValue.split('.').reduce((o, index) => o[index], x) === obj)
      }
else {
      
    selectedList = this.options.filter((item) => {
      return item[this.optionValue] === obj;
    });
  }

    return selectedList.length > 0 ? selectedList[0] : null;
  }

  /**
   * for the value to be set, get the listItem from the list bound to dropdown else return null
   * @param obj value to be written to the dropdown
   */
  getMultiselectOption(obj: any[]) {
    const multiSelectValue = [];
    if (obj && obj.length > 0) {
      for (const selectedValue of obj) {
        for (const option of this.options) {
          if (typeof selectedValue === 'string' && option[this.optionValue] === selectedValue.trim()) {
            multiSelectValue.push(option);
          }
        }
      }
    }
    return multiSelectValue.length > 0 ? multiSelectValue : obj;
  }

  /**
   * handler change function
   * @param fn change fn
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * handler touch function
   * @param fn touch fn
   */
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  /**
   * set control to disabled state
   * @param disabled is control disabled
   */
  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
    this.renderer.setAttribute(this.element.nativeElement, 'disabled', disabled.toString());
  }

  /**
   * change event of the dropdown
   */
  change() {
    // Angular does not know that the value has changed
    // from our component, so we need to update her with the new value.
    const returnVal = this.optionValue && !this.isMultiSelect && this._value ? this.getOptionValue() : this._value;
    this.changeEvent.emit(returnVal);
  }

  /**
   * get option value
   */
  getOptionValue() {
    return this.optionValue.split('.').reduce((i, key) => {
      return i[key];
    }, this._value);
  }

  /**
   * on blur event, call touch event
   */
  blur() {
    this.onTouch();
  }

  /**
   * add select option
   */
  addSelect() {
    if (!this.isMultiSelect) {
      if (!this.options.find((o) => o[this.label] === 'Select')) {
        const obj = Object.assign({}, this.options[0]);
        obj[this.label] = 'Select';
        obj[this.optionValue] = '';
        this.options.unshift(obj);
      }
    }
  }

  /**
   * bind the value once the list is available
   * @param changes changes event
   */
  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.options &&
      changes.options.currentValue !== changes.options.previousValue &&
      !changes.options.firstChange &&
      this.valueToWrite
    ) {
      this.writeValue(this.valueToWrite);
      this.valueToWrite = '';
    }
  }

  /**
   * Get html value
   * @param value value to be sanitized as html
   */
  getHtml(value) {
    return this.sanitizer.bypassSecurityTrustHtml(value.replace(/<<n>>/g, '<br/>'));
  }

  /**
   * descr for tooltip
   */
  getTooltipDescr() {
    const selectedList = this.options?.filter((item) => {
      return this.value && item[this.optionValue] === this.value[this.optionValue];
    });

    return selectedList?.length > 0 ? selectedList[0][this.label] : '';
  }

  /**
   * focus listener
   */
  @HostListener('focus')
  focusHandler() {
    alert('focused!');
  }
}
